/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { typography2 } from '@coursera/cds-core';

import type { EnterpriseLoggedInPageHeader_EnterpriseProgramFragment as Program } from 'bundles/composable-page-header-enterprise/queries/__generated__/EnterpriseLoggedInMyPageHeaderQuery';
import { EnterpriseSearchBarImpl } from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchBarV2';
import MobileEnterpriseSearchBar, {
  UnifiedMobileEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/MobileEnterpriseSearchBar';
import type { SearchScope } from 'bundles/enterprise-learner-search/components/search/types';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';

import _t from 'i18n!nls/composable-page-header-enterprise';

type Props = {
  currentProgram?: Partial<Program> | null;
  enterprisePrograms?: Partial<Program>[] | null;
};

const styles = {
  searchBar: css`
    && {
      .rc-EnterpriseSearchBarV2 {
        max-width: 480px;
        min-width: 0;
        width: 100%;

        input {
          /* Needs important to override old style that's also set to important. Yuck! */
          font: ${typography2.bodySecondary.font} !important;
          letter-spacing: ${typography2.bodySecondary.letterSpacing};
        }
      }
    }
  `,
};

export function EnterpriseMobileSearchBarLegacyGridItem(props: Props) {
  const { currentProgram, enterprisePrograms } = props;
  const enterpriseProductFeatures = useEnterpriseProductFeatures();
  const userId = userGet()?.id;

  const enableSkillsInSearchAndBrowse = enterpriseProductFeatures?.enableSkillsInSearchAndBrowse;
  const shouldShowShortFormContent = enterpriseProductFeatures?.allowShortFormContentBasedOnAudience;

  const thirdPartyOrg = currentProgram?.naptimeThirdPartyOrganization;
  const thirdPartyOrganizationSlug = currentProgram?.naptimeThirdPartyOrganization?.slug;

  const programSlug = currentProgram?.slug;
  const programName = currentProgram?.name;
  const programId = currentProgram?.id;

  const programIds: Array<string> = [];
  const scopes: Array<SearchScope> | undefined = enterprisePrograms
    ?.map((tmp) => {
      const programIdTmp = tmp?.id ?? '';
      programIds.push(programIdTmp);
      const { name: programName0, slug: programSlug0 } = tmp;
      return {
        trackingData: { programSlug: programSlug0 ?? '' },
        pathname: `/programs/${programSlug0}`,
        placeholder: _t("Search in '#{programName}'...", { programName0 }),
        title: programName0 ?? '',
        programIds: [programIdTmp],
      };
    })
    .sort((a, b) => a.placeholder.localeCompare(b.placeholder));
  scopes?.unshift({
    trackingData: { thirdPartyOrgSlug: thirdPartyOrganizationSlug ?? '' },
    pathname: `/programs/all/${thirdPartyOrganizationSlug}`,
    placeholder: _t('Search in all programs you joined...'),
    title: _t('All Learning Programs'),
    programIds,
  });

  if (userId && programName && programSlug && programId && thirdPartyOrg) {
    return (
      <UnifiedMobileEnterpriseSearchBar
        programSlug={programSlug}
        programName={programName}
        thirdPartyOrgSlug={thirdPartyOrg.slug}
        thirdPartyOrganizationId={thirdPartyOrg.id}
        userId={userId}
        programId={programId}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
      />
    );
  } else if (programId) {
    return (
      <MobileEnterpriseSearchBar
        programIds={[programId]}
        enableSkillsInSearchAndBrowse={!!enableSkillsInSearchAndBrowse}
        programName={programName}
        shouldShowShortFormContent={shouldShowShortFormContent}
      />
    );
  } else if (programIds) {
    return (
      <div css={styles.searchBar}>
        <EnterpriseSearchBarImpl
          initialScopeIndex={0}
          scopes={scopes}
          enableSkillsInSearchAndBrowse={!!enableSkillsInSearchAndBrowse}
          shouldShowShortFormContent={shouldShowShortFormContent}
        />
      </div>
    );
  } else {
    return null;
  }
}

export default EnterpriseMobileSearchBarLegacyGridItem;
